import { FileSystemNavigator } from '../utils/filesystem';
import { getAssetPath } from './assetPath';

declare global {
  interface Window {
    __MAPLEOS_ENV?: Record<string, string>;
  }
}

export type BootEffects = {
  setTheme: (dark: boolean) => void;
  setWallpaper: (url: string) => void;
  notify: (title: string, message: string) => void;
};

type ConfigEntries = Record<string, string>;

function parseKeyValLines(text: string): ConfigEntries {
  const lines = text.split(/\r?\n/);
  const entries: ConfigEntries = {};
  for (const raw of lines) {
    const line = raw.trim();
    if (!line || line.startsWith(';') || line.toUpperCase().startsWith('REM')) continue;
    const eqIdx = line.indexOf('=');
    if (eqIdx > -1) {
      const key = line.slice(0, eqIdx).trim().toUpperCase();
      const val = line.slice(eqIdx + 1).trim();
      entries[key] = val;
    } else {
      // handle directives without "=" like "FILES 30" (not standard here, but safe)
      const parts = line.split(/\s+/);
      if (parts.length >= 2) {
        const key = parts[0].trim().toUpperCase();
        const val = parts.slice(1).join(' ').trim();
        entries[key] = val;
      }
    }
  }
  return entries;
}

function pickWallpaperUrl(input: string): string {
  const val = input.trim();
  if (val.startsWith('builtin:')) {
    const id = val.replace(/^builtin:/, '');
    // map built-ins to our bundled wallpapers
    const map: Record<string, string> = {
      default: '/os/assets/images/wallpapers/drops_maple_leaf_autumn_1319773_1600x900.jpg',
      edmonton: '/os/assets/images/wallpapers/edmonton_canada_night_city_118462_1600x900.jpg',
      owl: '/os/assets/images/wallpapers/owl_white_owl_polar_owl_136287_1600x900.jpg',
      toronto: '/os/assets/images/wallpapers/city_buildings_architecture_160579_1600x900.jpg',
      deer: '/os/assets/images/wallpapers/deer_wildlife_horns_131368_1600x900.jpg',
      cubes: '/os/assets/images/wallpapers/cubes_edges_abstraction_1312232_1600x900.jpg',
    };
    return getAssetPath(map[id] ?? map.default);
  }
  // otherwise assume URL or app-relative file path works in CSS background
  return getAssetPath(val);
}

async function applyConfigSys(configText: string | undefined, effects: BootEffects) {
  if (!configText) return;
  const entries = parseKeyValLines(configText);

  // theme
  const theme = entries['THEME'];
  if (theme) {
    effects.setTheme(theme.toLowerCase() === 'dark');
    effects.notify('config.sys', `Theme set to ${theme.toLowerCase()}`);
  }

  // wallpaper
  const wall = entries['WALLPAPER'];
  if (wall) {
    const url = pickWallpaperUrl(wall);
    effects.setWallpaper(url);
    effects.notify('config.sys', `Wallpaper applied`);
  }

  // environment PATH
  const path = entries['PATH'];
  if (path) {
    window.__MAPLEOS_ENV = window.__MAPLEOS_ENV || {};
    window.__MAPLEOS_ENV['PATH'] = path;
    effects.notify('config.sys', `PATH set: ${path}`);
  }

  // FILES/BUFFERS just stored for reference
  const files = entries['FILES'];
  const buffers = entries['BUFFERS'];
  if (files) {
    window.__MAPLEOS_ENV = window.__MAPLEOS_ENV || {};
    window.__MAPLEOS_ENV['FILES'] = files;
  }
  if (buffers) {
    window.__MAPLEOS_ENV = window.__MAPLEOS_ENV || {};
    window.__MAPLEOS_ENV['BUFFERS'] = buffers;
  }
}

function detectAppForPath(path: string): { appId: string; title: string } {
  const name = path.split('\\').pop() || 'File';
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (['txt', 'md', 'csv', 'json', 'js', 'ts', 'tsx', 'css', 'html'].includes(ext)) {
    return { appId: 'notepad', title: `Notepad - ${name}` };
  }
  if (ext === 'mapleslide') {
    return { appId: 'maple-slide', title: `MapleSlide - ${name}` };
  }
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
    return { appId: 'image-viewer', title: `Image Viewer - ${name}` };
  }
  if (ext === 'wav') {
    return { appId: 'music-player', title: `Music Player - ${name}` };
  }
  return { appId: 'notepad', title: `Notepad - ${name}` };
}

async function runAutoexec(autoText: string | undefined, navigator: FileSystemNavigator, effects: BootEffects) {
  if (!autoText) return;
  const lines = autoText.split(/\r?\n/);
  let echoOn = true;

  const setEnv = (key: string, val: string) => {
    window.__MAPLEOS_ENV = window.__MAPLEOS_ENV || {};
    window.__MAPLEOS_ENV[key] = val;
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const upper = line.toUpperCase();

    if (upper.startsWith('REM ')) continue;
    if (upper === '@ECHO OFF') { echoOn = false; continue; }

    // SET KEY=VALUE
    if (upper.startsWith('SET ')) {
      const kv = line.slice(4);
      const idx = kv.indexOf('=');
      if (idx > -1) {
        const key = kv.slice(0, idx).trim();
        const val = kv.slice(idx + 1).trim();
        setEnv(key, val);
        if (key.toUpperCase() === 'PATH') {
          effects.notify('autoexec.bat', `PATH updated: ${val}`);
        }
      }
      continue;
    }

    // ECHO message
    if (upper.startsWith('ECHO ')) {
      if (echoOn) {
        const msg = line.slice(5);
        effects.notify('autoexec.bat', msg);
      }
      continue;
    }

    // THEME/WALLPAPER directives for extra flair
    if (upper.startsWith('THEME ')) {
      const val = line.slice(6).trim().toLowerCase();
      effects.setTheme(val === 'dark');
      effects.notify('autoexec.bat', `Theme set to ${val}`);
      continue;
    }

    if (upper.startsWith('WALLPAPER ')) {
      const val = line.slice(10).trim();
      const url = pickWallpaperUrl(val);
      effects.setWallpaper(url);
      effects.notify('autoexec.bat', `Wallpaper applied`);
      continue;
    }

    // MKDIR
    if (upper.startsWith('MKDIR ')) {
      const dir = line.slice(6).trim();
      await navigator.createFolder(dir);
      if (echoOn) effects.notify('autoexec.bat', `Created directory: ${dir}`);
      continue;
    }

    // COPY src dest (files only, simple copy)
    if (upper.startsWith('COPY ')) {
      const parts = line.slice(5).split(/\s+/);
      if (parts.length >= 2) {
        const src = parts[0];
        const dest = parts[1];
        const srcItem = navigator.findItemByPath(src);
        const content = srcItem?.content ?? '';
        await navigator.createFile(dest, content);
        if (echoOn) effects.notify('autoexec.bat', `Copied: ${src} → ${dest}`);
      }
      continue;
    }

    // DEL path (moves to recycle bin via navigator)
    if (upper.startsWith('DEL ')) {
      const target = line.slice(4).trim();
      await navigator.deleteItem(target);
      if (echoOn) effects.notify('autoexec.bat', `Deleted: ${target}`);
      continue;
    }

    // START app [optional path]
    if (upper.startsWith('START ')) {
      const rest = line.slice(6).trim();
      const parts = rest.split(/\s+/);
      const appId = parts[0];
      const filePath = parts.slice(1).join(' ') || undefined;
      const title = filePath ? `${appId} - ${filePath.split('\\').pop()}` : appId;
      window.dispatchEvent(new CustomEvent('operator:openWindow', { detail: { appId, title, filePath } }));
      if (echoOn) effects.notify('autoexec.bat', `Starting ${appId}${filePath ? ` (${filePath})` : ''}`);
      continue;
    }

    // OPEN path -> choose correct app by extension
    if (upper.startsWith('OPEN ')) {
      const path = line.slice(5).trim();
      const app = detectAppForPath(path);
      window.dispatchEvent(new CustomEvent('operator:openWindow', { detail: { appId: app.appId, title: app.title, filePath: path } }));
      if (echoOn) effects.notify('autoexec.bat', `Opening ${path}`);
      continue;
    }

    // Unknown: just show a small message (quietly)
    // effects.notify('autoexec.bat', `Ignored: ${line}`);
  }
}

/**
 * Run boot scripts once per session:
 * - Apply config from C:\config.sys
 * - Execute commands from C:\autoexec.bat
 */
export async function runBootScripts(navigator: FileSystemNavigator, effects: BootEffects): Promise<void> {
  await navigator.waitForInitialization();
  const configItem = navigator.findItemByPath('C:\\config.sys');
  const autoItem = navigator.findItemByPath('C:\\autoexec.bat');

  const configText = configItem?.content;
  const autoText = autoItem?.content;

  await applyConfigSys(configText, effects);
  await runAutoexec(autoText, navigator, effects);

  // Final flourish
  //effects.notify('MapleOS', 'Boot scripts executed');
}